/**
 * Pure derivations for the Summary screen's heatmap/gamaka/drift views (Task 6).
 * Built entirely from Phase 3's `SessionReport` (+ the raw recorded samples for
 * `driftSeries`) — no new scoring, just reshaping numbers already computed by
 * `analyzeSession`/`gradePitch`. Kept free of DOM/Canvas so they're unit-tested
 * directly; `results-panel.ts` (Phase 4b's single-page redesign) renders their
 * output — the pre-redesign `screens/summary.ts` this originally served has
 * since been deleted.
 */
import {
  JI_RATIOS,
  TRANSITION_VELOCITY_CPS,
  gradePitch,
  pitchVelocityCents,
  smoothPitchHz,
} from '@swarasense/shared';
import type { PracticeLevel, RecordedSample, SessionReport, SwaraSymbol } from '@swarasense/shared';

export type HeatmapGrade = 'excellent' | 'good' | 'off' | 'wrong';

export interface HeatmapCell {
  sym: string;
  grade: HeatmapGrade;
  inTunePct: number;
  meanCents: number;
}

/** Ascending-pitch sort key for the 12-symbol swara set (falls back to the raw
 * string, unchanged, for anything outside it — defensive only; every symbol
 * `analyzeSession` produces is one of `SWARA_SYMBOLS`). */
function byPitch(a: string, b: string): number {
  const ra = JI_RATIOS[a as SwaraSymbol];
  const rb = JI_RATIOS[b as SwaraSymbol];
  if (ra === undefined || rb === undefined) return a.localeCompare(b);
  return ra - rb;
}

/** Share of in-tune frames at/above which a swara reads as "excellent". */
const EXCELLENT_INTUNE_PCT = 75;
/** Share of in-tune frames at/above which a swara reads as "good" (below EXCELLENT). */
const GOOD_INTUNE_PCT = 45;
/** Share of in-tune frames at/above which a swara still reads as "off" rather
 * than "wrong" (a rarely-centred swara that was at least sometimes visited). */
const OFF_INTUNE_PCT = 15;
/** Beyond this |meanCents|, the swara is systematically mis-centred, not just
 * scattered — caps the grade at "off" even when inTunePct alone would read
 * excellent/good (a few frames landing in the excellent band doesn't redeem
 * a note whose average sits this far from its target). */
const SYSTEMATIC_BIAS_CENTS = 60;

/**
 * Grades one swara's stats into the four-band heatmap scale. `inTunePct` drives
 * the base grade (see thresholds above); a large systematic bias (`meanCents`
 * beyond `SYSTEMATIC_BIAS_CENTS`) can only ever pull the grade down to "off",
 * and a swara that's both rarely centred AND badly biased grades "wrong".
 */
function gradeSwara(inTunePct: number, meanCents: number): HeatmapGrade {
  const centred = Math.abs(meanCents) <= SYSTEMATIC_BIAS_CENTS;
  if (inTunePct >= EXCELLENT_INTUNE_PCT && centred) return 'excellent';
  if (inTunePct >= GOOD_INTUNE_PCT && centred) return 'good';
  if (inTunePct >= OFF_INTUNE_PCT || centred) return 'off';
  return 'wrong';
}

/**
 * Per-swara heatmap cells from the report's `perSwara` stats, in ascending
 * pitch order (a natural reading order for a heatmap, independent of
 * `perSwara`'s own best-to-worst sort). Swaras never sung don't appear —
 * there's no stat to grade.
 */
export function heatmapCells(report: SessionReport): HeatmapCell[] {
  return [...report.perSwara]
    .sort((a, b) => byPitch(a.symbol, b.symbol))
    .map((s) => ({
      sym: s.symbol,
      grade: gradeSwara(s.inTunePct, s.meanCents),
      inTunePct: s.inTunePct,
      meanCents: s.meanCents,
    }));
}

export interface GamakaBreakdownRow {
  swara: string;
  heldFlat: number;
  wavered: number;
}

/** Per-swara held-flat/wavered counts from `report.gamaka.flagged`, in
 * ascending pitch order. A swara with no flags at all doesn't appear. */
export function gamakaBreakdown(report: SessionReport): GamakaBreakdownRow[] {
  const counts = new Map<string, { heldFlat: number; wavered: number }>();
  for (const flag of report.gamaka.flagged) {
    const row = counts.get(flag.swara) ?? { heldFlat: 0, wavered: 0 };
    if (flag.verdict === 'held-flat') row.heldFlat++;
    else row.wavered++;
    counts.set(flag.swara, row);
  }
  return [...counts.entries()]
    .sort((a, b) => byPitch(a[0], b[0]))
    .map(([swara, c]) => ({ swara, ...c }));
}

export interface DriftPoint {
  /** Seconds from session start, at the bucket's midpoint. */
  tSec: number;
  /** Share of this bucket's frames graded "excellent" (0–100) — a fatigue
   * signal. `null` means the bucket had no non-transition (held-note) frames
   * at all — a silence or an all-glide stretch — which is "no data", NOT
   * "0% centred"; renderers must show a gap/"—", never a 0. */
  centeredPct: number | null;
}

/**
 * Buckets the session into `buckets` equal-width time windows and reports the
 * share of each window's frames graded "excellent" by `gradePitch` — the same
 * per-frame grading `analyzeSession` uses for `centeredPct`, just split out
 * over time instead of collapsed to one number, so a fading centre (or a
 * warm-up improving one) shows as a trend rather than an average.
 *
 * Pitch is smoothed the same way `analyzeSession` smooths it
 * (`smoothPitchHz`) before grading, so a gamaka wobble reads as its centre
 * swara rather than smearing across bands. Frames whose smoothed-pitch
 * velocity exceeds `TRANSITION_VELOCITY_CPS` (a note-to-note glide, not a
 * held note) are excluded from both the numerator AND denominator of each
 * bucket — mirroring `analyzeSession`'s own `centeredPct` (computed over
 * `frames.filter(f => !f.transition)`), so a phrase with a lot of movement
 * between notes doesn't read as a false dip in centring. Empty input or a
 * non-positive `buckets` count yields `[]`.
 */
export function driftSeries(
  recSamples: readonly RecordedSample[],
  saHz: number,
  targetCents: Record<string, number>,
  level: PracticeLevel,
  buckets: number,
): DriftPoint[] {
  if (recSamples.length === 0 || buckets <= 0) return [];
  const sorted = [...recSamples].sort((a, b) => a.t - b.t);
  const t0 = sorted[0]!.t;
  const durationMs = Math.max(1, sorted[sorted.length - 1]!.t - t0);
  const bucketMs = durationMs / buckets;

  const counts = Array.from({ length: buckets }, () => ({ total: 0, excellent: 0 }));
  let prev: { hz: number; t: number } | null = null;
  for (let i = 0; i < sorted.length; i++) {
    const s = sorted[i]!;
    const smoothHz = smoothPitchHz(sorted, i, saHz);
    const transition =
      prev !== null &&
      Math.abs(pitchVelocityCents(prev.hz, prev.t / 1000, smoothHz, s.t / 1000)) >
        TRANSITION_VELOCITY_CPS;
    prev = { hz: smoothHz, t: s.t };
    if (transition) continue; // a glide, not a held note — excluded like analyzeSession's centeredPct

    const grade = gradePitch(smoothHz, saHz, targetCents, level);
    const idx = Math.min(buckets - 1, Math.max(0, Math.floor((s.t - t0) / bucketMs)));
    counts[idx]!.total++;
    if (grade.band === 'excellent') counts[idx]!.excellent++;
  }

  return counts.map((c, i) => ({
    tSec: ((i + 0.5) * bucketMs) / 1000,
    // A bucket with zero held-note frames (silence, or entirely a glide) has
    // nothing to grade — `null`, not 0, so it renders as a gap/"—" rather
    // than misreporting "0% centred" (which would read as "sung, but badly
    // off" instead of "nothing to measure here").
    centeredPct: c.total > 0 ? (100 * c.excellent) / c.total : null,
  }));
}

/** Minimum percentage-point drop (below BOTH the first and last valid bucket)
 * for a mid-session low point to count as a real "dip" worth calling out,
 * rather than ordinary bucket-to-bucket noise. */
const DIP_THRESHOLD_PCT = 15;

/**
 * A short, plain-language description of how well-centred% moved across a
 * session's drift buckets — for the drift chart's aria-label. Looking only
 * at the first and last bucket (the original approach) mislabels a session
 * that dipped in the middle and recovered as flatly "improved" or "faded",
 * even though the shape in between told a different story; this instead
 * finds the lowest valid bucket and calls out a real mid-session dip when
 * one exists. Buckets with no data (`centeredPct === null`, see `driftSeries`)
 * are skipped entirely rather than misread as a dip to zero.
 */
export function driftTrendDescription(points: readonly DriftPoint[]): string {
  const valid = points.filter(
    (p): p is DriftPoint & { centeredPct: number } => p.centeredPct !== null,
  );
  if (valid.length < 2) return 'not enough voiced data across the take to show a trend';

  const first = valid[0]!.centeredPct;
  const last = valid[valid.length - 1]!.centeredPct;
  let minIdx = 0;
  for (let i = 1; i < valid.length; i++) {
    if (valid[i]!.centeredPct < valid[minIdx]!.centeredPct) minIdx = i;
  }
  const min = valid[minIdx]!.centeredPct;
  const isMidDip =
    minIdx > 0 &&
    minIdx < valid.length - 1 &&
    min <= first - DIP_THRESHOLD_PCT &&
    min <= last - DIP_THRESHOLD_PCT;

  if (isMidDip) {
    return last >= first ? 'dipped mid-session, then recovered' : 'dipped mid-session and did not fully recover';
  }
  return last >= first ? 'held steady or improved' : 'faded from start to finish';
}
